import type { ApiResponse, PaginatedResponse } from '@/types'
import { getStoredAdminUser, isApiAllowedForUser } from './adminPermission'
import { createApiRequestError } from './apiError'
import { getEnvConfig } from './runtime-env'

class ApiClient {
  private token: string | null = null

  constructor() {
    // 从localStorage获取token
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('admin_token')
    }
  }

  private getBaseURL(): string {
    return getEnvConfig().API_BASE_URL
  }

  private getUploadCandidates(endpoint: string): string[] {
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return [endpoint]
    }

    const config = getEnvConfig()
    const apiBase = config.API_BASE_URL
    const uploadBase = config.UPLOAD_API_BASE_URL
    const isAdminUpload = endpoint.includes('/admin/files/upload')
    const isVisualGenerationUpload = endpoint.includes('/admin/visual-generation/upload')
    const candidates: string[] = []

    if (isVisualGenerationUpload) {
      candidates.push(`${apiBase}${endpoint}`)
    } else if (isAdminUpload) {
      if (uploadBase) {
        candidates.push(`${uploadBase}/api/v1/files/upload`)
        candidates.push(`${uploadBase}${endpoint}`)
      }

      candidates.push(`${apiBase}${endpoint}`)
      candidates.push(`${apiBase}/api/v1/files/upload`)
    } else {
      candidates.push(`${this.getBaseURL()}${endpoint}`)
    }

    return [...new Set(candidates)]
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const requestMethod = (options.method || 'GET').toUpperCase()
    const currentUser = getStoredAdminUser()
    if (!isApiAllowedForUser(currentUser, requestMethod, endpoint)) {
      throw new Error('当前账号无权访问该接口')
    }

    const url = `${this.getBaseURL()}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }
    if (typeof window !== 'undefined' && window.location.host) {
      headers['X-Admin-Web-Host'] = window.location.host
    }

    // 每次请求时都从localStorage获取最新的token
    const currentToken =
      typeof window !== 'undefined' ? localStorage.getItem('admin_token') : this.token

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      const rawText = await response.text()
      const contentType = response.headers.get('content-type') || ''
      let data: ApiResponse<T>

      if (!rawText) {
        data = {
          code: response.ok ? 0 : response.status,
          message: '',
          data: undefined as T,
        }
      } else if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(rawText) as ApiResponse<T>
        } catch {
          throw new Error(`接口返回了非法 JSON：${rawText.slice(0, 160)}`)
        }
      } else {
        const trimmed = rawText.trim()
        if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
          throw new Error(
            `接口返回了 HTML 页面，通常是 API 地址配置错误。当前请求：${url}。本地 admin-api 默认应为 http://localhost:8081`
          )
        }
        try {
          data = JSON.parse(trimmed) as ApiResponse<T>
        } catch {
          throw new Error(`接口返回了非 JSON 内容：${trimmed.slice(0, 160)}`)
        }
      }

      if (!response.ok) {
        throw createApiRequestError(data, response.status, `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async get<T>(
    endpoint: string,
    options?: {
      params?: Record<string, unknown> | object
      headers?: Record<string, string>
      signal?: AbortSignal
    }
  ): Promise<ApiResponse<T>> {
    let url = endpoint
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url = `${endpoint}?${queryString}`
      }
    }
    return this.request<T>(url, {
      method: 'GET',
      headers: options?.headers,
      signal: options?.signal,
    })
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(
    endpoint: string,
    options?:
      | { params?: Record<string, unknown>; headers?: Record<string, string> }
      | { body?: unknown; headers?: Record<string, string> }
  ): Promise<ApiResponse<T>> {
    let url = endpoint
    let body: string | undefined
    if (options && 'params' in options && options.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) url = `${endpoint}?${queryString}`
    } else if (options && 'body' in options && options.body !== undefined) {
      body = JSON.stringify(options.body)
    }
    return this.request<T>(url, { method: 'DELETE', body, headers: options?.headers })
  }

  // 分页请求
  async getPaginated<T>(
    endpoint: string,
    params?: {
      page?: number
      page_size?: number
      [key: string]: unknown
    }
  ): Promise<ApiResponse<PaginatedResponse<T>>> {
    const searchParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
    }

    const url = searchParams.toString() ? `${endpoint}?${searchParams.toString()}` : endpoint
    return this.get<PaginatedResponse<T>>(url)
  }

  // 设置认证token
  setToken(token: string) {
    this.token = token
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin_token', token)
    }
  }

  // 清除token
  clearToken() {
    this.token = null
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin_token')
    }
  }

  // 获取当前token
  getToken(): string | null {
    return this.token
  }

  // 文件上传方法
  async upload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    // 添加额外的表单数据
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    const headers: Record<string, string> = {}

    // 获取token
    const currentToken =
      typeof window !== 'undefined' ? localStorage.getItem('admin_token') : this.token

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`
    }

    const attemptUpload = async (url: string): Promise<ApiResponse<T>> => {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      })

      const rawText = await response.text()
      const contentType = response.headers.get('content-type') || ''
      let data: ApiResponse<T>

      if (!rawText) {
        data = {
          code: response.ok ? 0 : response.status,
          message: '',
          data: undefined as T,
        }
      } else if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(rawText) as ApiResponse<T>
        } catch {
          throw new Error(`上传接口返回了非法 JSON：${rawText.slice(0, 160)}`)
        }
      } else {
        const trimmed = rawText.trim()
        if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
          throw new Error(
            `上传接口返回了 HTML 页面，通常是 API 地址配置错误。当前请求：${url}。本地 admin-api 默认应为 http://localhost:8081`
          )
        }
        try {
          data = JSON.parse(trimmed) as ApiResponse<T>
        } catch {
          throw new Error(`上传接口返回了非 JSON 内容：${trimmed.slice(0, 160)}`)
        }
      }

      if (!response.ok) {
        throw createApiRequestError(data, response.status, `HTTP error! status: ${response.status}`)
      }

      return data
    }

    const candidates = this.getUploadCandidates(endpoint)
    let lastError: unknown

    for (const url of candidates) {
      try {
        return await attemptUpload(url)
      } catch (error) {
        lastError = error
        const message = error instanceof Error ? error.message : String(error)
        const shouldTryNext =
          candidates.length > 1 &&
          (message.includes('status: 404') ||
            message.includes('status: 405') ||
            message.includes('接口返回了 HTML 页面'))

        if (!shouldTryNext) {
          console.error('Upload request failed:', error)
          throw error
        }
      }
    }

    console.error('Upload request failed:', lastError)
    throw lastError instanceof Error ? lastError : new Error('上传失败')
  }

  async uploadFiles<T>(
    endpoint: string,
    files: File[],
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    const headers: Record<string, string> = {}
    const currentToken =
      typeof window !== 'undefined' ? localStorage.getItem('admin_token') : this.token

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`
    }

    const attemptUpload = async (url: string): Promise<ApiResponse<T>> => {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      })

      const rawText = await response.text()
      const contentType = response.headers.get('content-type') || ''
      let data: ApiResponse<T>

      if (!rawText) {
        data = {
          code: response.ok ? 0 : response.status,
          message: '',
          data: undefined as T,
        }
      } else if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(rawText) as ApiResponse<T>
        } catch {
          throw new Error(`上传接口返回了非法 JSON：${rawText.slice(0, 160)}`)
        }
      } else {
        const trimmed = rawText.trim()
        if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
          throw new Error(
            `上传接口返回了 HTML 页面，通常是 API 地址配置错误。当前请求：${url}。本地 admin-api 默认应为 http://localhost:8081`
          )
        }
        try {
          data = JSON.parse(trimmed) as ApiResponse<T>
        } catch {
          throw new Error(`上传接口返回了非 JSON 内容：${trimmed.slice(0, 160)}`)
        }
      }

      if (!response.ok) {
        throw createApiRequestError(data, response.status, `HTTP error! status: ${response.status}`)
      }

      return data
    }

    const candidates = this.getUploadCandidates(endpoint)
    let lastError: unknown

    for (const url of candidates) {
      try {
        return await attemptUpload(url)
      } catch (error) {
        lastError = error
        const message = error instanceof Error ? error.message : String(error)
        const shouldTryNext =
          candidates.length > 1 &&
          (message.includes('status: 404') ||
            message.includes('status: 405') ||
            message.includes('接口返回了 HTML 页面'))

        if (!shouldTryNext) {
          console.error('Upload request failed:', error)
          throw error
        }
      }
    }

    console.error('Upload request failed:', lastError)
    throw lastError instanceof Error ? lastError : new Error('上传失败')
  }
}

export const apiClient = new ApiClient()
export default apiClient
